"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import StandardPage from '@/components/StandardPage';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ProductItem, getProducts, getProductsFiltered } from '@/lib/supabase';
import cache from '@/lib/cache/productsCache';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Package, Search, ChevronDown, PlusCircle, Book, Smartphone, Truck, Image as ImageIcon, Shirt, Grid, Home, Film, Loader2 } from '@/components/icons';
import { Plane } from 'lucide-react';
import MultiStepProductForm from '@/components/MultiStepProductForm';
import CreateStoreForm from '@/components/CreateStoreForm';
import StoreList from '@/components/StoreList';
import StoresPage from './StoresPage';
import StoreDashboard from '@/components/store/StoreDashboard';
import { createProduct } from '@/lib/supabase';
import ProductChipsReorderModal from '@/components/ProductChipsReorderModal';
import { useToast } from '@/components/ui/use-toast';
import useDebounce from '@/hooks/use-debounce';
import { useSession } from '@/context/SessionContext';
import ProductCard from '@/components/ProductCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'; // Import Tabs components
import MyProductListings from '@/components/MyProductListings'; // Import MyProductListings
import { useWakeLock } from '@/hooks/useWakeLock';
import PullToRefresh from '@/components/PullToRefresh';

// Distance calculation function (Haversine formula)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const BrowseProductsPage: React.FC = () => {
    // Refrescar feed al recibir evento global
    useEffect(() => {
      const handler = () => {
        setManualSearchTerm('');
        setDateFilter('any');
        setRefreshTrigger((prev) => prev + 1);
      };
      window.addEventListener('browse-refresh-requested', handler);
      return () => window.removeEventListener('browse-refresh-requested', handler);
    }, []);

    // Navegar a producto desde evento de maximizar sidebar
    useEffect(() => {
      const handler = (e: any) => {
        const productId = e?.detail?.productId;
        if (productId) {
          window.history.pushState({}, '', `/products/${productId}`);
          // Opcional: podrías cargar el producto en pantalla completa aquí si tienes lógica SPA
        }
      };
      window.addEventListener('product-sidebar-maximize', handler);
      return () => window.removeEventListener('product-sidebar-maximize', handler);
    }, []);
  const { session, loading: sessionLoading, profile, relevantLocation } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Control tabs and inline publish form
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('all-products');
  const [showPublishForm, setShowPublishForm] = useState<boolean>(!!searchParams.get('publish'));
  const [creating, setCreating] = useState<boolean>(false);
  const [publishProductType, setPublishProductType] = useState<string | null>(null);

  // Log mount and unmount
  useEffect(() => {
    return () => {
    };
  }, []);

  // Mantener la pantalla encendida mientras se navega el marketplace
  useWakeLock(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [storeRefreshKey, setStoreRefreshKey] = useState<number>(0);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Scroll to the form when it is rendered (i.e., when a category has been selected)
    if (publishProductType && formRef.current) {
      try {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        // Ignore if scroll fails in some environments
      }
    }
  }, [publishProductType]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [distance, setDistance] = useState<string>('50');
  const [dateFilter, setDateFilter] = useState<string>('any');
  const [priceMin, setPriceMin] = useState<number | null>(null);
  const [priceMax, setPriceMax] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const { toast } = useToast();

  // Manually trigger search when user clicks the Search button
  const [manualSearchTerm, setManualSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(manualSearchTerm, 500);

  // Read category from URL params
  const category = searchParams.get('category') || 'any';

  // Usar relevantLocation del contexto (unifica Supabase y localStorage)
  const userLocation = relevantLocation;

  const PAGE_SIZE = 10;
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchProducts = useCallback(async (isInitial = true) => {
    if (isInitial) setLoadingProducts(true);
    try {
      // Try server-side filtered fetch first (better for scale).
      try {
        const opts = {
          q: debouncedSearchTerm || null,
          category: category !== 'any' ? category : null,
          seller_id: null,
          min_price: priceMin ?? null,
          max_price: priceMax ?? null,
          lat: userLocation?.latitude ?? null,
          lng: userLocation?.longitude ?? null,
          max_km: distance ? parseInt(distance, 10) : null,
          date_filter: dateFilter ?? null,
          limit: 50,
          offset: 0,
        };
        const cacheKey = await cache.generateCacheKey(opts as any);
        try {
          const cached = await cache.getCachedProducts(cacheKey, 1000 * 60 * 60 * 24);
          if (cached && Array.isArray(cached) && cached.length > 0) {
            setProducts(cached as ProductItem[]);
          }
        } catch (e) {
          console.warn('[CACHE] Error reading cache for BrowseProductsPage:', e);
        }

        const fetchedProducts = await getProductsFiltered(opts as any);
        setProducts(fetchedProducts);
        setDisplayCount(PAGE_SIZE); // Reset pagination on refresh
      } catch (rpcErr) {
        console.warn('[FETCH_PRODUCTS] RPC fetch failed, falling back to client fetch:', rpcErr);
        const fetchedProducts = await getProducts(debouncedSearchTerm);
        setProducts(fetchedProducts);
        setDisplayCount(PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error al cargar productos",
        description: "No pudimos cargar los productos. Probá de nuevo.",
        variant: "destructive",
      });
    } finally {
      if (isInitial) setLoadingProducts(false);
      window.dispatchEvent(new CustomEvent('browse-refresh-complete'));
    }
  }, [debouncedSearchTerm, toast, category, distance, priceMin, priceMax, dateFilter, userLocation?.latitude, userLocation?.longitude, session?.user?.id]);

  if (sessionLoading) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const activeProducts = products.filter(p => p.status !== 'deleted');
  
  // Apply local filters: category, search term, and distance
  const displayedProducts = activeProducts.filter((p) => {
    // Filter by category
    // If 'Míos' selected, filter by current user's products
    if (category === 'mine') {
      if (!session?.user?.id) return false;
      if (p.seller_id !== session.user.id) return false;
    } else if (category !== 'any') {
      const allowed = category === 'groceries' ? ['groceries', 'other'] : [category];
      if (!allowed.includes(p.product_type)) return false;
    }
    // Filter by search term in title or author (if available)
    if (manualSearchTerm) {
      const searchLower = manualSearchTerm.toLowerCase();
      const titleMatch = p.title?.toLowerCase().includes(searchLower);
      const authorMatch = p.author?.toLowerCase().includes(searchLower);
      if (!titleMatch && !authorMatch) return false;
    }
    // Filter by distance if user explicitly selected a distance and product location available
    if (distance && userLocation && p.latitude && p.longitude) {
      const dist = calculateDistance(userLocation.latitude, userLocation.longitude, p.latitude, p.longitude);
      const maxDistance = parseInt(distance, 10);
      if (!Number.isFinite(maxDistance) || dist > maxDistance) return false;
    }

    // Filter by price range (sale price preferred, otherwise rental)
    const price = Number(p.fixed_sale_price ?? p.fixed_rental_price ?? 0) || 0;
    if (priceMin !== null && price < priceMin) return false;
    if (priceMax !== null && price > priceMax) return false;

    // Filter by publication date
    if (dateFilter && dateFilter !== 'any' && p.created_at) {
      const created = new Date(p.created_at).getTime();
      const now = Date.now();
      let cutoff = now;
      if (dateFilter === '24h') cutoff = now - 24 * 60 * 60 * 1000;
      else if (dateFilter === '7d') cutoff = now - 7 * 24 * 60 * 60 * 1000;
      else if (dateFilter === '30d') cutoff = now - 30 * 24 * 60 * 60 * 1000;
      else if (dateFilter === '365d') cutoff = now - 365 * 24 * 60 * 60 * 1000;
      if (created < cutoff) return false;
    }
    return true;
  });

  const handleSearch = () => {
    setManualSearchTerm(searchTerm);
    setShowFilters(false);
  };

  const handleProductUpdate = useCallback((updatedProduct: ProductItem) => {
    setProducts(prevProducts =>
      prevProducts.map(p => (p.id === updatedProduct.id ? updatedProduct : p))
    );
  }, []);

  const handleCreateProduct = async (productData: any) => {
    if (!session?.user?.id) {
      toast({ title: "Error", description: "Debes iniciar sesión para publicar.", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      const result = await createProduct(
        session.user.id,
        productData.title,
        productData.author || null,
        productData.description || null,
        productData.main_image_url || null, // cover_url
        productData.fixed_rental_price || null,
        productData.fixed_sale_price || null,
        productData.free_shipping || false,
        productData.is_auction || false,
        productData.auction_start_time || null,
        productData.auction_end_time || null,
        productData.starting_bid || null,
        productData.buy_now_price || null,
        productData.product_type || 'other',
        productData.condition || null,
        productData.image_urls || [],
        productData.main_image_url || null,
        productData.status || 'available',
        productData.details || {},
        productData.rental_options || null,
        productData.latitude || null,
        productData.longitude || null,
        productData.location_name || null,
        productData.store_id || null
      );
      if (result && (result as any).id) {
        toast({ title: "Publicado!", description: "Tu producto ya está disponible." });
        setRefreshTrigger(prev => (prev || 0) + 1);
        setShowPublishForm(false);
        setPublishProductType(null);
      }
    } catch (err: any) {
      console.error("Error creating product:", err);
      toast({ title: "Error al publicar", description: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchProducts(true);
  }, [fetchProducts, refreshTrigger]);

  // Infinite scroll observer for products
  useEffect(() => {
    if (!sentinelRef.current || displayCount >= displayedProducts.length || loadingMore) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setLoadingMore(true);
          setTimeout(() => {
            setDisplayCount(c => {
              const next = Math.min(displayedProducts.length, c + PAGE_SIZE);
              setLoadingMore(false);
              return next;
            });
          }, 600);
        }
      });
    }, { rootMargin: '200px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [displayedProducts.length, displayCount, loadingMore]);

  useEffect(() => {
    try {
      const backendCount = products.length;
      const frontendCount = displayedProducts.length;
      const backendIds = products.map((p) => p.id).filter(Boolean);
      const frontendIds = displayedProducts.map((p) => p.id).filter(Boolean);

      // For any backend product not displayed, compute and log rejection reason
      const missing = backendIds.filter((id) => !frontendIds.includes(id));
      if (missing.length > 0) {
        missing.forEach((id) => {
          const p = products.find((x) => x.id === id);
          if (!p) return;
          const reasons: string[] = [];
          // status
          if (p.status === 'deleted') reasons.push('status=deleted');
          // category
          if (category !== 'any' && p.product_type !== category) reasons.push(`category=${p.product_type}`);
          // search term
          if (manualSearchTerm) {
            const searchLower = manualSearchTerm.toLowerCase();
            const titleMatch = p.title?.toLowerCase().includes(searchLower);
            const authorMatch = p.author?.toLowerCase().includes(searchLower);
            if (!titleMatch && !authorMatch) reasons.push('search_no_match');
          }
          // distance
          if (distance && userLocation && p.latitude && p.longitude) {
            const dist = calculateDistance(userLocation.latitude, userLocation.longitude, p.latitude, p.longitude);
            const maxDistance = parseInt(distance, 10);
            if (!Number.isFinite(maxDistance)) reasons.push('distance_invalid');
            else if (dist > maxDistance) reasons.push(`distance_${Math.round(dist)}km>` + maxDistance + 'km');
          }
          // price
          const price = Number(p.fixed_sale_price ?? p.fixed_rental_price ?? 0) || 0;
          if (priceMin !== null && price < priceMin) reasons.push(`price_too_low:${price}`);
          if (priceMax !== null && price > priceMax) reasons.push(`price_too_high:${price}`);
          // date
          if (dateFilter && dateFilter !== 'any' && p.created_at) {
            const created = new Date(p.created_at).getTime();
            const now = Date.now();
            let cutoff = now;
            if (dateFilter === '24h') cutoff = now - 24 * 60 * 60 * 1000;
            else if (dateFilter === '7d') cutoff = now - 7 * 24 * 60 * 60 * 1000;
            else if (dateFilter === '30d') cutoff = now - 30 * 24 * 60 * 60 * 1000;
            else if (dateFilter === '365d') cutoff = now - 365 * 24 * 60 * 60 * 1000;
            if (created < cutoff) reasons.push('date_too_old');
          }

        });
      }
    } catch (err) {
      // ignore
    }
  }, [products, displayedProducts]);

  const onPullRefresh = React.useCallback(async () => {
    return new Promise<void>((resolve) => {
      const handleDone = () => {
        window.removeEventListener('browse-refresh-complete', handleDone);
        resolve();
      };
      window.addEventListener('browse-refresh-complete', handleDone);
      setRefreshTrigger(prev => (prev || 0) + 1);
      setTimeout(() => {
        window.removeEventListener('browse-refresh-complete', handleDone);
        resolve();
      }, 4000);
    });
  }, []);

  return (
    <PullToRefresh onRefresh={onPullRefresh}>
      <div className="w-full">
        <h1 className="sr-only">Tiendas</h1>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          if (v === 'all-products') navigate('/tiendas');
          else if (v === 'my-stores') navigate('/tiendas/mis-tiendas');
        }}
        className="w-full"
      >
        <StandardPage>
          <TabsContent value="all-products">
          <div className="mt-4 pb-20">
          {loadingProducts ? (
            <div className="flex flex-col items-center justify-center p-20 min-h-[50vh]">
              <Loader2 className="h-10 w-10 animate-spin text-primary opacity-70" />
              <p className="mt-4 text-muted-foreground font-medium">Cargando productos...</p>
            </div>
          ) : displayedProducts.length === 0 ? (
            <p className="text-muted-foreground text-center p-8">
              No se encontraron productos. ¡Sé el primero en publicar uno!
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {displayedProducts.slice(0, displayCount).map((product) => {
                if (!product || !product.id) {
                  console.warn("BrowseProductsPage: Se encontró un producto nulo o sin ID. Ignorando.", product);
                  return null;
                }
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    currentUserId={session?.user?.id}
                    session={session} // Pass session here
                    onProductUpdate={handleProductUpdate}
                  />
                );
              })}
              
              <div ref={sentinelRef} className="h-20 flex items-center justify-center">
                {loadingMore && (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary opacity-60" />
                    <span className="text-xs text-muted-foreground font-medium">Cargando más productos...</span>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </TabsContent>

        <TabsContent value="my-listings">
          <div className="mb-4">
            {!showPublishForm ? (
              <div>
                <Button onClick={() => { setActiveTab('my-listings'); setShowPublishForm(true); }} className="w-full hover:bg-primary/90">
                  <PlusCircle className="h-4 w-4 mr-2" /> Agregar anuncio
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="font-medium">Publicar nuevo anuncio</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowPublishForm(false); setPublishProductType(null); }} className="ml-2">Cerrar</Button>
              </div>
            )}
          </div>

          {showPublishForm && (
            <>
              <div className="mb-4">
                <div className="grid grid-cols-4 grid-rows-2 gap-3" role="radiogroup" aria-label="Tipo de producto">
                  {[
                    { key: 'book', label: 'Libro', icon: Book },
                    { key: 'electronics', label: 'Electrónica', icon: Smartphone },
                    { key: 'vehicle', label: 'Vehículo', icon: Truck },
                    { key: 'propiedades', label: 'Propiedades', icon: Home },
                    { key: 'diversion', label: 'Diversión', icon: Film },
                    { key: 'art', label: 'Arte', icon: ImageIcon },
                    { key: 'clothing', label: 'Ropa', icon: Shirt },
                    { key: 'groceries', label: 'Almacén', icon: Grid },
                    { key: 'tours', label: 'Excursiones', icon: Plane },
                    { key: 'tours', label: 'Excursiones', icon: Plane },
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      role="radio"
                      aria-checked={publishProductType === key}
                      onClick={() => setPublishProductType(key)}
                      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-md border transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${publishProductType === key ? 'bg-primary text-primary-foreground border-transparent' : 'bg-transparent border hover:bg-transparent hover:text-foreground'}`}
                      aria-label={label}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-xs mt-1">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Only render the form after the user picks a category */}
              {publishProductType && (
                <div ref={formRef} className="mb-6">
                  <MultiStepProductForm
                    session={session}
                    profile={profile}
                    initialProduct={null}
                    onSubmit={handleCreateProduct}
                    loading={creating}
                    submitButtonText="Publicar Producto"
                    initialProductType={publishProductType}
                  />
                </div>
              )}
            </>
          )}

          {!showPublishForm && (
            <MyProductListings />
          )}

        </TabsContent>

        <TabsContent value="my-stores">
            <StoresPage />
        </TabsContent>
      </StandardPage>
    </Tabs>
  </div>
  </PullToRefresh>
);
};

export default BrowseProductsPage;